import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, CalendarClock, Car as CarIcon } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { fmtDT, inr } from "@/lib/brand";
import { RequireAuth, StatusBadge } from "@/components/site/Shared";
import { RatingBadge } from "@/components/site/Stars";

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-5">
      <Icon className="h-6 w-6 text-[#FF5A00]" />
      <p className="mt-3 text-xs uppercase tracking-[0.15em] text-neutral-500">{label}</p>
      <p className="mt-1 text-2xl font-bold" data-testid={testId}>{value}</p>
    </div>
  );
}

function HostCars({ cars }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {cars.map((c) => (
        <div key={c.id} className="overflow-hidden rounded-2xl border border-black/5 bg-white" data-testid={`host-car-${c.id}`}>
          <img src={c.image} alt={c.name} className="aspect-[16/10] w-full object-cover" />
          <div className="space-y-1 p-4">
            <div className="flex items-center justify-between gap-2"><p className="font-semibold">{c.name}</p><RatingBadge avg={c.rating_avg} count={c.rating_count} /></div>
            <p className="text-sm text-neutral-600">Your share {c.share_pct}% · {c.trips} trip{c.trips === 1 ? "" : "s"} · earned {inr(c.earned)}</p>
            <p className={`text-xs font-semibold ${c.active ? "text-emerald-600" : "text-neutral-400"}`}>{c.active ? "Listed on website" : "Not listed"}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function HostBookings({ rows }) {
  if (!rows.length) return <p className="rounded-2xl border border-dashed border-black/10 bg-white p-8 text-center text-sm text-neutral-500" data-testid="host-bookings-empty">No bookings for your cars yet.</p>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-black/5 bg-white">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
          <tr>{["Booking", "Car", "Trip", "Customer", "Status", "Your earning"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-black/5">
          {rows.map((b) => (
            <tr key={b.id} data-testid={`host-booking-${b.id}`}>
              <td className="px-4 py-3 text-xs text-neutral-500">{b.id}</td>
              <td className="px-4 py-3 font-medium">{b.car_name}</td>
              <td className="px-4 py-3 text-xs">{fmtDT(b.pickup_at)} → {fmtDT(b.return_at)}</td>
              <td className="px-4 py-3">{b.customer}</td>
              <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
              <td className="px-4 py-3 font-semibold">{inr(b.earning)}{b.status !== "COMPLETED" && <span className="ml-1 text-xs font-normal text-neutral-400">(expected)</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HostInner() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/host/summary").then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, [api]);
  if (error) return (
    <div className="container-x max-w-xl py-24 text-center" data-testid="host-not-linked">
      <h1 className="text-2xl font-bold">No cars linked yet</h1>
      <p className="mt-3 text-neutral-600">{error}. If you'd like to list your car, send us your details.</p>
      <Link to="/host" className="mt-6 inline-block rounded-full bg-[#FF5A00] px-6 py-2.5 font-semibold text-white">Become a host</Link>
    </div>
  );
  if (!data) return <div className="container-x py-24 text-neutral-500">Loading…</div>;
  return (
    <div className="container-x space-y-10 py-12" data-testid="host-dashboard">
      <div>
        <p className="label-eyebrow">Host dashboard</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">Your cars & earnings</h1>
        <p className="mt-2 text-sm text-neutral-500">Earnings are your share of the rental charge (excluding GST, trip protection, driver and convenience fees).</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat icon={Wallet} label="Earned (completed trips)" value={inr(data.totals.earned)} testId="host-total-earned" />
        <Stat icon={CalendarClock} label="Expected (upcoming)" value={inr(data.totals.upcoming)} testId="host-total-upcoming" />
        <Stat icon={CarIcon} label="Completed trips" value={data.totals.trips} testId="host-total-trips" />
      </div>
      <section className="space-y-4"><h2 className="text-xl font-semibold">Your cars</h2><HostCars cars={data.cars} /></section>
      <section className="space-y-4"><h2 className="text-xl font-semibold">Bookings</h2><HostBookings rows={data.bookings} /></section>
    </div>
  );
}

export default function HostDashboard() {
  return <RequireAuth><HostInner /></RequireAuth>;
}
